// Clients service. Fase B: con backend real consulta Supabase (RLS aísla el
// tenant); sin variables de entorno usa fixtures (demo/CI). Ante un error real
// NO se muestran datos falsos: lista vacía / null y se registra el aviso.
import type { NewClientInput } from '@/lib/schemas/client';
import { supabase, hasBackend } from '@/lib/supabase';
import { toPrefixTsQuery } from '@/lib/search';
import { loadFailed } from './loadError';
import { fmtDate } from '@/lib/format';

export interface ClientSummary {
  id: string;
  name: string;
  email: string;
  phone: string;
  type: 'individual' | 'business';
  pendingDocuments: number;
  returnStatus: 'signed' | 'in_review' | 'received' | 'delivered' | 'pending';
  lastActivity: string;
}

export interface ClientProfile extends ClientSummary {
  address: string;
  maritalStatus: 'single' | 'married' | 'divorced' | 'widowed' | 'separated';
  dependents: { id: string; name: string; relation: string; birthYear: number }[];
  documents: { id: string; title: string; status: string; uploadedAt: string }[];
  returns: { id: string; year: number; status: string }[];
  timeline: { id: string; text: string; when: string }[];
}

/** Tamaño de página de la lista de clientes (VLT-312). */
export const CLIENTS_PAGE = 50;

export interface ClientsService {
  list(query?: string, page?: { limit?: number; offset?: number }): Promise<ClientSummary[]>;
  getProfile(id: string): Promise<ClientProfile | null>;
  create(input: NewClientInput): Promise<ClientSummary>;
  /** VLT-367: corrección de datos de contacto por el staff — el trigger de
   *  auditoría registra el antes/después (páginas blancas se alimentan solas). */
  update(id: string, input: NewClientInput): Promise<boolean>;
  /** VLT-083: archivar = soft delete (deleted_at) — libera cupo del plan
   *  (así cuentan 0034/0035) y NADA se destruye: restaurable con un clic.
   *  La RLS no oculta archivados; el filtro vive en list/listArchived. */
  archive(id: string): Promise<boolean>;
  restore(id: string): Promise<boolean>;
  listArchived(query?: string): Promise<ClientSummary[]>;
}

const FIXTURES: ClientSummary[] = [
  { id: 'c1', name: 'María Santos', email: 'maria.santos@mail.com', phone: '(809) 555-0134', type: 'individual', pendingDocuments: 0, returnStatus: 'signed', lastActivity: 'hace 2 h' },
  { id: 'c2', name: 'Andrés Rivera', email: 'a.rivera@mail.com', phone: '(829) 555-0182', type: 'individual', pendingDocuments: 2, returnStatus: 'in_review', lastActivity: 'hace 4 h' },
  { id: 'c3', name: 'Lucía Gómez', email: 'lucia.g@mail.com', phone: '(849) 555-0117', type: 'individual', pendingDocuments: 1, returnStatus: 'received', lastActivity: 'ayer' },
  { id: 'c4', name: 'Núñez LLC', email: 'admin@nunezllc.com', phone: '(809) 555-0966', type: 'business', pendingDocuments: 3, returnStatus: 'delivered', lastActivity: 'ayer' },
  { id: 'c5', name: 'Kwame Osei', email: 'kwame.osei@mail.com', phone: '(305) 555-0224', type: 'individual', pendingDocuments: 1, returnStatus: 'pending', lastActivity: 'hace 40 min' },
  { id: 'c6', name: 'Diego Fuentes', email: 'diego.f@mail.com', phone: '(786) 555-0347', type: 'individual', pendingDocuments: 1, returnStatus: 'in_review', lastActivity: 'hace 2 h' },
  { id: 'c7', name: 'Innova LLC', email: 'contact@innova.io', phone: '(917) 555-0459', type: 'business', pendingDocuments: 0, returnStatus: 'pending', lastActivity: 'hoy 13:00' },
  { id: 'c8', name: 'Rosa Peralta', email: 'rosa.peralta@mail.com', phone: '(809) 555-0771', type: 'individual', pendingDocuments: 4, returnStatus: 'pending', lastActivity: 'hace 3 días' },
];

const ARCHIVED_FIXTURES: ClientSummary[] = [];

const PROFILE_EXTRAS: Omit<ClientProfile, keyof ClientSummary> = {
  address: 'Av. Winston Churchill 95, Santo Domingo',
  maritalStatus: 'married',
  dependents: [
    { id: 'd1', name: 'Sofía Santos', relation: 'Hija', birthYear: 2015 },
    { id: 'd2', name: 'Liam Santos', relation: 'Hijo', birthYear: 2019 },
  ],
  documents: [
    { id: '4', title: 'W-2 2025', status: 'expired', uploadedAt: 'hoy 09:12' },
    { id: '1', title: "Driver's License", status: 'expiring_soon', uploadedAt: 'hoy 08:44' },
    { id: '3', title: 'Residence Card', status: 'active', uploadedAt: '14 jul' },
  ],
  returns: [
    { id: 'r1', year: 2025, status: 'signed' },
    { id: 'r0', year: 2024, status: 'delivered' },
  ],
  timeline: [
    { id: 't1', text: 'Firmó la declaración 2025', when: 'hace 2 h' },
    { id: 't2', text: 'Subió W-2 desde el portal', when: 'hoy 09:12' },
    { id: 't3', text: 'Pagó servicio de preparación · $180', when: 'ayer' },
  ],
};

// ─── Implementación real (Supabase, RLS por tenant) ─────────────────────────

interface ClientRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  tags: string[];
  updated_at: string;
}

const rowToSummary = (r: ClientRow): ClientSummary => ({
  id: r.id,
  name: `${r.first_name} ${r.last_name}`.trim(),
  email: r.email ?? '',
  phone: r.phone ?? '',
  type: r.tags.includes('business') ? 'business' : 'individual',
  // Agregados (docs pendientes / estado de declaración) llegan con la vista de
  // resumen del próximo bloque; hasta entonces valores neutros.
  pendingDocuments: 0,
  returnStatus: 'pending',
  lastActivity: fmtDate(r.updated_at),
});

/** Divide "Nombre Apellido(s)" para el modelo first/last de la tabla. */
const splitName = (full: string): { first: string; last: string } => {
  const parts = full.trim().split(/\s+/);
  return { first: parts[0] ?? '', last: parts.slice(1).join(' ') || '—' };
};

const realClients: ClientsService = {
  async list(query = '', { limit = CLIENTS_PAGE, offset = 0 } = {}) {
    const q = query.trim();
    let req = supabase
      .from('clients')
      .select('id, first_name, last_name, email, phone, tags, updated_at')
      .is('deleted_at', null)
      .order('updated_at', { ascending: false })
      .range(offset, offset + limit - 1);
    // VLT-309: FTS server-side sobre el índice GIN (nombre+email+ciudad+tags),
    // con prefijos por palabra — "mar san" encuentra a María Santos.
    const tsq = q ? toPrefixTsQuery(q) : '';
    if (tsq) req = req.textSearch('search_vector', tsq, { config: 'simple' });
    const { data, error } = await req;
    if (error) loadFailed('clients.list', error);
    return (data as ClientRow[]).map(rowToSummary);
  },

  async getProfile(id) {
    const { data, error } = await supabase
      .from('clients')
      .select('id, first_name, last_name, email, phone, tags, updated_at, address_line1, city, marital_status')
      .eq('id', id)
      .maybeSingle();
    if (error) loadFailed('clients.getProfile', error);
    if (!data) return null; // de verdad no existe (o no es de esta firma)
    const row = data as ClientRow & { address_line1: string | null; city: string | null; marital_status: ClientProfile['maritalStatus'] | null };
    return {
      ...rowToSummary(row),
      address: [row.address_line1, row.city].filter(Boolean).join(', '),
      maritalStatus: row.marital_status ?? 'single',
      // Sub-recursos (dependientes, documentos, declaraciones, timeline) se
      // conectan con sus servicios en los próximos bloques.
      dependents: [],
      documents: [],
      returns: [],
      timeline: [],
    };
  },

  async create(input) {
    const { first, last } = splitName(input.fullName);
    const { data: orgId, error: orgError } = await supabase.rpc('current_org_id');
    if (orgError || !orgId) throw new Error(orgError?.message ?? 'no organization');
    const { data, error } = await supabase
      .from('clients')
      // 'as never': el placeholder de tipos no conoce columnas; se sustituye
      // al generar los tipos reales con `supabase gen types` (red permitida).
      .insert({
        organization_id: orgId as string,
        first_name: first,
        last_name: last,
        email: input.email,
        phone: input.phone,
        tags: input.type === 'business' ? ['business'] : [],
        // Pasaporte Universal (VLT-393): identidad de primera clase.
        client_kind: input.type === 'business' ? 'entity' : 'individual',
        entity_type: input.type === 'business' ? (input.entityType ?? 'other') : null,
        ein_last4: input.type === 'business' && input.einLast4 ? input.einLast4 : null,
      } as never)
      .select('id, first_name, last_name, email, phone, tags, updated_at')
      .single();
    if (error) throw new Error(error.message);
    return rowToSummary(data as ClientRow);
  },

  async update(id, input) {
    const { first, last } = splitName(input.fullName);
    const { error } = await supabase
      .from('clients')
      .update({
        first_name: first,
        last_name: last,
        email: input.email,
        phone: input.phone,
        tags: input.type === 'business' ? ['business'] : [],
        client_kind: input.type === 'business' ? 'entity' : 'individual',
        entity_type: input.type === 'business' ? (input.entityType ?? 'other') : null,
        ein_last4: input.type === 'business' && input.einLast4 ? input.einLast4 : null,
      } as never)
      .eq('id', id);
    if (error) {
      console.warn('[VaulTex] clients.update:', error.message);
      return false;
    }
    return true;
  },

  async archive(id) {
    const { error } = await supabase
      .from('clients')
      .update({ deleted_at: new Date().toISOString() } as never)
      .eq('id', id);
    if (error) console.warn('[VaulTex] clients.archive:', error.message);
    return !error;
  },

  async restore(id) {
    const { error } = await supabase
      .from('clients')
      .update({ deleted_at: null } as never)
      .eq('id', id);
    if (error) console.warn('[VaulTex] clients.restore:', error.message);
    return !error;
  },

  async listArchived(query = '') {
    const q = query.trim();
    let req = supabase
      .from('clients')
      .select('id, first_name, last_name, email, phone, tags, updated_at')
      .not('deleted_at', 'is', null)
      .order('updated_at', { ascending: false })
      .limit(CLIENTS_PAGE);
    const tsq = q ? toPrefixTsQuery(q) : '';
    if (tsq) req = req.textSearch('search_vector', tsq, { config: 'simple' });
    const { data, error } = await req;
    if (error) loadFailed('clients.listArchived', error);
    return (data as ClientRow[]).map(rowToSummary);
  },
};

// ─── Implementación demo (fixtures, Fase A) ──────────────────────────────────

const fixtureClients: ClientsService = {
  list: (query = '', { limit = CLIENTS_PAGE, offset = 0 } = {}) => {
    const q = query.trim().toLowerCase();
    const all = q ? FIXTURES.filter((c) => `${c.name} ${c.email}`.toLowerCase().includes(q)) : FIXTURES;
    return Promise.resolve(all.slice(offset, offset + limit));
  },
  getProfile: (id) => {
    const base = FIXTURES.find((c) => c.id === id);
    return Promise.resolve(base ? { ...base, ...PROFILE_EXTRAS } : null);
  },
  create: (input) => {
    const created: ClientSummary = {
      id: `c${FIXTURES.length + 1}`,
      name: input.fullName,
      email: input.email,
      phone: input.phone,
      type: input.type,
      pendingDocuments: 0,
      returnStatus: 'pending',
      lastActivity: 'ahora',
    };
    FIXTURES.unshift(created);
    return Promise.resolve(created);
  },
  update: (id, input) => {
    const c = FIXTURES.find((x) => x.id === id);
    if (!c) return Promise.resolve(false);
    c.name = input.fullName;
    c.email = input.email;
    c.phone = input.phone;
    c.type = input.type;
    return Promise.resolve(true);
  },
  archive: (id) => {
    const i = FIXTURES.findIndex((x) => x.id === id);
    if (i < 0) return Promise.resolve(false);
    ARCHIVED_FIXTURES.unshift(FIXTURES.splice(i, 1)[0]);
    return Promise.resolve(true);
  },
  restore: (id) => {
    const i = ARCHIVED_FIXTURES.findIndex((x) => x.id === id);
    if (i < 0) return Promise.resolve(false);
    FIXTURES.unshift(ARCHIVED_FIXTURES.splice(i, 1)[0]);
    return Promise.resolve(true);
  },
  listArchived: (query = '') => {
    const q = query.trim().toLowerCase();
    const all = q ? ARCHIVED_FIXTURES.filter((c) => `${c.name} ${c.email}`.toLowerCase().includes(q)) : ARCHIVED_FIXTURES;
    return Promise.resolve([...all]);
  },
};

export const clientsService: ClientsService = hasBackend ? realClients : fixtureClients;
